import { useQuery } from "@tanstack/react-query"

import { getRestaurantDetail } from "@/lib/client/api/restaurant"
import { Category, RestaurantDetail } from "@/lib/types"

/**
 * Hook to fetch and map restaurant dish details by their IDs
 * @param restaurantId The ID of the restaurant
 * @returns A query result containing a map of dish IDs to their full details, including images
 */
export function useRestaurantDetailLongCache(restaurantId: string | null) {
  return useQuery({
    queryKey: ["restaurant", "detail", restaurantId],
    queryFn: async (): Promise<{
      dishMap: Record<string, Category["items"][number]>
      restaurant: RestaurantDetail | null
    }> => {
      if (!restaurantId) return { dishMap: {}, restaurant: null }

      // Convert string ID to number for the API call
      const restaurantIdNumber = parseInt(restaurantId, 10)
      if (isNaN(restaurantIdNumber)) {
        return {
          dishMap: {},
          restaurant: null,
        }
      }

      const res = await getRestaurantDetail(restaurantIdNumber)
      const dishes = res.data.categories.map(
        (category: Category) => category.items
      )
      const allDishes = dishes.flat()

      return {
        dishMap: allDishes.reduce(
          (
            acc: Record<string, Category["items"][number]>,
            dish: Category["items"][number]
          ) => {
            // Use string keys since checkout uses string IDs
            acc[dish.restaurant_item_id.toString()] = dish
            return acc
          },
          {} as Record<string, Category["items"][number]>
        ),
        restaurant: res.data,
      }
    },
    enabled: !!restaurantId,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
  })
}
